import { makeStyles, MenuItem, Popover, Select, TextField } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { observer } from 'mobx-react';
import * as React from 'react';
import { mg } from '../../mg/mg';
import { useOmnistore } from '../../stores/Omnistore';
import { ISupplierItem } from '../entities';
import { transl } from '../transl';
import { _IEditProps } from './_IEditProps';

const useStyles = makeStyles({
	_AdVariationsEditDialog: {
		'& .MuiDialog-paper': {
			width: 400,
		},
	},
	variations: {
		margin: 0,
		padding: '10px 10px 20px',
		display: 'grid',
		gridTemplateColumns: '4fr 1fr auto',
		gap: '10px',
	},
	delete: {
	},
});

interface IVariation {
	readonly id: number
	readonly name: string
}

interface IRepo {
	allAsList: ReadonlyArray<ISupplierItem>,
}

interface IVariationViewProps {
	readonly index: number
	readonly variation: IVariation
	setVariation(index: number, variation: IVariation|null) : void
	canDelete: boolean
	repo: IRepo
	multiple: boolean
}

const VariationView = observer(function VariationView(props: IVariationViewProps) {
	const classes = useStyles();
	const {index, variation, setVariation, canDelete, repo, multiple} = props;
	return <>
		<Select
			required
			name="item"
			label={transl('item')}
			error={!variation.id}
			value={variation.id || ''}
			onChange={e => setVariation(index, {...variation, id: e.target.value as number})}
		>
			{repo.allAsList.slice().sort(mg.comparatorOn(i => i.name)).map(i => <MenuItem value={i.id} key={i.id}>{i.name} ­— {i.packaging}</MenuItem>)}
		</Select>
		<TextField
			required
			name="name"
			error={!variation.name}
			label={transl('variation')}
			value={variation.name}
			onChange={e => setVariation(index, {...variation, name: e.target.value})}
		/>
		{!multiple ? null :<DeleteOutlineIcon
			color={canDelete ? 'inherit' : 'disabled'}
			className={classes.delete}
			onClick={e => canDelete && setVariation(index, null)}
		/>}
	</>;
});

interface IVariationsDialogProps {
	readonly variations: ReadonlyArray<IVariation>
	setVariations(variations: ReadonlyArray<IVariation>) : void
	repo: IRepo
	multiple: boolean
}

const VariationsDialog = observer(function VariationsDialog(props: IVariationsDialogProps) {
	const classes = useStyles();
	const {variations, setVariations, multiple} = props;
	function setVariation(index: number, variation: IVariation|null) : void {
		const prefix = variations.slice(0, index);
		const e = variation ? [variation] : [];
		const suffix = variations.slice(index+1);
		setVariations(prefix.concat(e, suffix));
	}
	function addVariation(e: React.MouseEvent) {
		setVariations(variations.concat({id: 0, name: ''}));
	}
	return <div className={classes.variations}>
		{variations.map((e, i) => <VariationView key={i}
			index={i}
			variation={e}
			setVariation={setVariation}
			canDelete={multiple}
			repo={props.repo}
			multiple={multiple}
		/>)}
		{!multiple ? null : <AddIcon onClick={addVariation}/>}
	</div>;
});

export const _AdVariationsEdit = observer(function _AdVariationsEdit(props: _IEditProps & {multiple: boolean}) {
	const {accessors, entity, label, error, isRequired, isReadonly, className, variant, helperText, placeholder, multiple} = props;
	const value = accessors.getter() as unknown as ReadonlyArray<IVariation>;
	const repo = useOmnistore().multirepo.supplierItemRepo(entity.supplierId);
	const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement|null>(null);
	React.useEffect(() => void (entity.supplierId && repo.fetch()), [entity.supplierId]);
	const setVariations = (variations: ReadonlyArray<IVariation>) => accessors.setter(variations as any);
	const text = value.map(v => v.name || repo.byId(v.id)?.name || '...').join(', ');
	return <>
		<TextField
			className={className}
			disabled={isReadonly}
			error={error}
			helperText={helperText}
			label={label}
			required={isRequired}
			value={text}
			variant={variant}
			placeholder={placeholder}
			onClick={e => setAnchorEl(e.currentTarget)}
		/>
		{!anchorEl ? null : <Popover
			open={!!anchorEl}
			onClose={() => setAnchorEl(null)}
			anchorEl={anchorEl}
			anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
			transformOrigin={{vertical: 'top', horizontal: 'left'}}
		>
			<VariationsDialog variations={value} setVariations={setVariations} repo={repo} multiple={multiple}/>
		</Popover>}
	</>;
});
